/**
 * PRNG con semilla explícita. El caso diario depende de que la generación sea
 * determinista y reproducible, así que en el motor **nunca** se usa `Math.random()`.
 */

export interface Rng {
  /** Real en `[0, 1)`. */
  next(): number;
  /** Entero en `[0, maxExclusive)`. */
  int(maxExclusive: number): number;
  /** Baraja en su sitio (Fisher-Yates) y devuelve el mismo array. */
  shuffle<T>(items: T[]): T[];
  /** Elemento al azar. Lanza si el array está vacío. */
  pick<T>(items: readonly T[]): T;
}

/** Mulberry32: 32 bits de estado, suficiente y muy rápido. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Hash de texto a semilla de 32 bits (variante de xmur3). */
export function hashSeed(seed: string): number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i += 1) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  h = Math.imul(h ^ (h >>> 16), 2246822507);
  h = Math.imul(h ^ (h >>> 13), 3266489909);
  return (h ^= h >>> 16) >>> 0;
}

export function createRng(seed: string | number): Rng {
  const numeric = typeof seed === 'number' ? seed >>> 0 : hashSeed(seed);
  const next = mulberry32(numeric);
  return {
    next,
    int(maxExclusive) {
      if (maxExclusive <= 0) throw new RangeError('int(maxExclusive) exige max > 0');
      return Math.floor(next() * maxExclusive);
    },
    shuffle(items) {
      for (let i = items.length - 1; i > 0; i -= 1) {
        const j = Math.floor(next() * (i + 1));
        const a = items[i] as (typeof items)[number];
        const b = items[j] as (typeof items)[number];
        items[i] = b;
        items[j] = a;
      }
      return items;
    },
    pick(items) {
      if (items.length === 0) throw new RangeError('pick() sobre un array vacío');
      return items[Math.floor(next() * items.length)] as (typeof items)[number];
    },
  };
}
